import { useEffect, useRef } from 'react';
import { Send } from 'lucide-react';
import { Button } from '@/components/ui';

export function ChatComposer({
  value,
  onChange,
  onSend,
  isLoading,
  hasAgent,
}: {
  value: string;
  onChange: (v: string) => void;
  onSend: () => void;
  isLoading: boolean;
  hasAgent: boolean;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);

  // Auto-grow up to ~8 lines
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [value]);

  const disabled = isLoading || !hasAgent;

  return (
    <div className="shrink-0 border-t border-border bg-card px-4 py-3 sm:px-6 sm:py-4">
      <div className="mx-auto max-w-3xl">
        <div className="flex items-end gap-2 rounded-xl border border-input bg-card p-1.5 pl-3 shadow-card transition-colors focus-within:border-sl-charcoal focus-within:ring-2 focus-within:ring-sl-yellow/60">
          <textarea
            ref={ref}
            rows={1}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                onSend();
              }
            }}
            placeholder={hasAgent ? 'Stel een vraag over je data…' : 'Kies eerst een agent'}
            disabled={disabled}
            aria-label="Bericht"
            className="max-h-[200px] min-h-[2.25rem] flex-1 resize-none bg-transparent py-2 text-sm leading-relaxed text-foreground placeholder:text-muted-foreground/70 focus:outline-none disabled:cursor-not-allowed"
          />
          <Button
            variant="primary"
            onClick={onSend}
            disabled={isLoading || !value.trim() || !hasAgent}
            loading={isLoading}
            className="h-9 shrink-0"
            aria-label="Versturen"
          >
            {!isLoading && <Send className="h-4 w-4" />}
            <span className="hidden sm:inline">{isLoading ? 'Bezig…' : 'Versturen'}</span>
          </Button>
        </div>
        <p className="mt-1.5 hidden text-center text-xs text-muted-foreground sm:block">
          Enter om te versturen · Shift+Enter voor een nieuwe regel
        </p>
      </div>
    </div>
  );
}
